import React, { useState, useEffect, useRef } from 'react'
import { Card } from 'react-bootstrap'
import { FileText, Download, Eye, Clock } from 'lucide-react'
import { useLazyQuery } from '@apollo/react-hooks'
import { demandsQueries } from '../../../graphql/queries'
import { Link } from 'react-router-dom'
import Spinner from 'ui-components/lib/components/Spinner'
import { useTheme } from '../../../contexts/ThemeContext'
import './PrivacyDocuments.css'

const PrivacyDocuments = ({ clientId }) => {
  const [documents, setDocuments] = useState([])
  const { setTheme } = useTheme()
  const previousThemeRef = useRef(null)

  const formatDate = (date) => {
    if (!date) return ''
    // Tratar tanto timestamps (Float) quanto strings de data
    const d = typeof date === 'number' ? new Date(date) : new Date(date)
    if (isNaN(d.getTime())) return ''
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  }

  const handleViewDocument = (documentos) => {
    if (documentos && documentos.length > 0 && documentos[0].fileURL) {
      window.open(documentos[0].fileURL, '_blank')
    }
  }

  const handleDownloadDocument = (documentos) => {
    if (documentos && documentos.length > 0 && documentos[0].fileURL) {
      const link = document.createElement('a')
      link.href = documentos[0].fileURL
      link.download = documentos[0].name || documentos[0].fileName || 'documento'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const [fetchCheckDataPrivacyClientDocuments, { loading: loadingGetCheckDataPrivacyClientDocuments }] = useLazyQuery(
    demandsQueries.GET_DATAPRIVACY_CHECKOUT_FILES,
    {
      onCompleted(data) {
        const files = data?.getCheckDataPrivacyClientDocuments?.data || []
        const formattedDocuments = files.map((file, index) => {
          return {
            id: file._id || index,
            title: file.description || '',
            type: file.documentos[0]?.name || '',
            version: file.version || '',
            date: formatDate(file.createdAt || new Date()),
            status: file.status || '',
            file: file
          }
        })
        setDocuments(formattedDocuments)
      }
    }
  )

  // Guarda o tema atual e força o tema escuro ao entrar na tela
  useEffect(() => {
    // Guarda o tema atual em uma variável local (lê do localStorage)
    const currentTheme = localStorage.getItem('app-theme') || 'light'
    previousThemeRef.current = currentTheme
    
    // Força o tema escuro
    if (currentTheme !== 'dark') {
      setTheme('dark')
    }
    
    // Cleanup: restaura o tema anterior ao sair da tela
    return () => {
      if (previousThemeRef.current && previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current)
      }
    }
  }, [setTheme]) // setTheme é estável do contexto, não causa re-renders

  useEffect(() => {
    if (clientId !== undefined) {
      fetchCheckDataPrivacyClientDocuments({
        variables: {
          clientId: clientId
        }
      })
    }
  }, [clientId, fetchCheckDataPrivacyClientDocuments])

  return (
    <Card className="privacy-documents-card">
      <div className="privacy-documents-header">
        <div className="privacy-documents-title-section">
          <FileText className="privacy-documents-title-icon" />
          <h4 className="privacy-documents-title">Documentos de Privacidade</h4>
        </div>
      </div>
      <div className="privacy-documents-content">
        {loadingGetCheckDataPrivacyClientDocuments ? (
          <div className="privacy-documents-loading">
            <Spinner color="primary" />
          </div>
        ) : documents.length > 0 ? (
          <div className="privacy-documents-list">
            {documents.map((doc) => (
              <div key={doc.id} className="privacy-documents-item">
                <div className="privacy-documents-item-content">
                  <div className="privacy-documents-item-header">
                    <h5 className="privacy-documents-item-title">{doc.title}</h5>
                    <span className={`privacy-documents-item-badge ${doc.status === 'Aprovado' ? 'approved' : 'review'}`}>
                      {doc.status}
                    </span>
                  </div>
                  <div className="privacy-documents-item-meta">
                    <span>{doc.type}</span>
                    <span>•</span>
                    <span>{doc.version}</span>
                    <span>•</span>
                    <div className="privacy-documents-item-date">
                      <Clock className="privacy-documents-item-date-icon" />
                      {doc.date}
                    </div>
                  </div>
                </div>
                <div className="privacy-documents-item-actions">
                  {doc.file.documentos && doc.file.documentos.length > 0 && doc.file.documentos[0].fileURL ? (
                    <>
                      <button 
                        className="privacy-documents-action-btn" 
                        title="Visualizar"
                        onClick={() => handleViewDocument(doc.file.documentos)}
                      >
                        <Eye className="privacy-documents-action-icon" />
                      </button>
                      <button 
                        className="privacy-documents-action-btn" 
                        title="Download"
                        onClick={() => handleDownloadDocument(doc.file.documentos)}
                      >
                        <Download className="privacy-documents-action-icon" />
                      </button>
                    </>
                  ) : (
                    <span className="privacy-documents-no-files" style={{ fontSize: '12px', color: '#999' }}>
                      Sem arquivo
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="privacy-documents-empty">
            <p>Nenhum documento encontrado</p>
            <Link to="/dashboard/documentos">
              <button className="privacy-documents-empty-btn">Acessar documentos</button>
            </Link>
          </div>
        )}
      </div>
    </Card>
  )
}

export default PrivacyDocuments
